import { ChevronRight, ScreenShare, Smartphone } from "lucide-react"
import { memo, useCallback } from "react"
import { setPreviewModal } from "../../../../../redux/slices/integrations"
import { Integration, IntegrationType } from "../../../../../types"
import { useAppDispatch } from "../../../../hooks"
import IntegrationStatusTag from "../components/IntegrationStatusTag"
import IntegrationTypeIcon from "../components/IntegrationTypeIcon"

interface Props {
  data: Integration
  onSelect?: (id: string) => void
}

const TYPE_LABELS: Record<IntegrationType, string> = {
  api: "API embed",
  boomtown: "LO page embed",
  widget: "Full embed",
  standalone: "Standalone embed",
  chatbot: "Website chat embed",
  button: "Button embed",
  standalone_pricing: "Standalone pricing"
}

function getFeatureTags(integration: Integration): string[] {
  const { type, app_settings } = integration

  if (type === "chatbot") return ["Chat"]

  if (!app_settings) return []

  const tags: string[] = []
  if (app_settings.lead_workflow) tags.push("Lead Workflow")
  if (app_settings.rate_table_config) tags.push("Rate Table")
  if (app_settings.settings?.rateCardsConfigId) tags.push("Rate Cards")
  if (app_settings.mort_calc || app_settings.afford_calc)
    tags.push("Calculators")
  if (app_settings.settings?.loReviews || app_settings.settings?.brandReviews)
    tags.push("Reviews")
  if (app_settings.settings?.contactForm) tags.push("Contact Form")
  if (app_settings.settings?.aboutBoxes) tags.push("About Boxes")
  return tags
}

const IntegrationCell = memo(({ data, onSelect }: Props) => {
  const dispatch = useAppDispatch()
  const { title, type, app_key, id } = data
  const tags = getFeatureTags(data)
  const typeLabel = TYPE_LABELS[type] ?? type

  const openEditPanel = useCallback(() => {
    onSelect?.(id)
  }, [onSelect, id])

  const handleDesktopPreview = useCallback(() => {
    dispatch(
      setPreviewModal({ isOpen: true, app_key, title, device: "desktop" })
    )
  }, [dispatch, app_key, title])

  const handleMobilePreview = useCallback(() => {
    dispatch(
      setPreviewModal({ isOpen: true, app_key, title, device: "mobile" })
    )
  }, [dispatch, app_key, title])

  return (
    <td className="py-4 pl-2 pr-3">
      <div className="flex items-center gap-4">
        <IntegrationTypeIcon type={type} />

        <div
          className="flex-1 min-w-0 cursor-pointer"
          onClick={openEditPanel}
          tabIndex={0}
          role="button"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-base text-base-dark hover:underline whitespace-nowrap">
              {title}
            </span>
            <span className="text-sm text-base-dark whitespace-nowrap">
              | {typeLabel} |
            </span>
            <IntegrationStatusTag integrationId={id} />
          </div>

          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-sm text-base-dark">App key</span>
            <span className="bg-[#f5f5f5] px-3 py-1 font-mono text-sm text-base-dark whitespace-nowrap">
              {app_key}
            </span>
            {tags.length > 0 && (
              <span className="size-1 rounded-full bg-[#c4c4c4] shrink-0" />
            )}
            {tags.map(tag => (
              <span
                key={tag}
                className="bg-[#f8fafc] border border-[#e2e8f0] rounded-md px-3 py-1.5 text-xs font-medium text-[#343c49] whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-6 ml-auto shrink-0">
          {type !== "api" && (
            <>
              <button
                onClick={handleDesktopPreview}
                type="button"
                className="flex items-center gap-2 text-primary-400 font-bold text-sm hover:opacity-75 transition-opacity focus:outline-none"
              >
                <ScreenShare size={16} />
                <span className="hidden lg:inline">Desktop preview</span>
              </button>
              <button
                onClick={handleMobilePreview}
                type="button"
                className="flex items-center gap-2 text-primary-400 font-bold text-sm hover:opacity-75 transition-opacity focus:outline-none"
              >
                <Smartphone size={16} />
                <span className="hidden lg:inline">Mobile preview</span>
              </button>
            </>
          )}
          <button onClick={openEditPanel}>
            <ChevronRight className="text-neutral-400" size={24} />
          </button>
        </div>
      </div>
    </td>
  )
})

IntegrationCell.displayName = "IntegrationCell"

export default IntegrationCell
